import { useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import "../../styles/home.css";
import "../../styles/how-prufyy-works.css";
import { initScrollReveal } from "../../utils/scrollReveal";
import { initScrollProgress } from "../../utils/scrollProgress";

function Accelerator() {
  useEffect(() => {
    initScrollReveal();
    initScrollProgress();
  }, []);

  return (
    <>
      <Header />

      {/* SCROLL PROGRESS */}
      <div className="scroll-progress">
        <div className="scroll-progress-fill" />
      </div>

      <main className="hpw-wrapper">
        {/* ================= HERO ================= */}
        <section className="hero-section reveal">
          <div className="hero-content">
            <h1>
              Acceleration Is About Speed <br />
              But Decisions Still Need Signal
            </h1>
            <p>
              Accelerators operate under intense time pressure. Hundreds of
              startups, compressed programs, and rapid decisions amplify the
              cost of unclear credibility.
            </p>
          </div>
        </section>

        {/* ================= AUDIENCE ================= */}
        <section className="section reveal">
          <span className="audience-badge">Accelerator Perspective</span>
          <h2>For Accelerators Running High-Velocity Programs</h2>
          <p>
            Speed magnifies everything. Strong signals stand out faster, but so
            do weak assumptions when structure is missing.
          </p>
        </section>

        {/* ================= PROBLEM ================= */}
        <section className="section reveal">
          <h2>Where Accelerator Signal Breaks Down</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Application Overload</h4>
              <p>Large volumes mask real differentiation.</p>
            </div>

            <div className="info-card">
              <h4>Noisy Mentor Feedback</h4>
              <p>Input becomes inconsistent across reviewers.</p>
            </div>

            <div className="info-card">
              <h4>Polish Over Substance</h4>
              <p>Decisions lean too heavily on narrative quality.</p>
            </div>

            <div className="info-card">
              <h4>Demo Day Bias</h4>
              <p>Presentations replace real diligence.</p>
            </div>

            <div className="info-card">
              <h4>Post-Program Surprises</h4>
              <p>Credibility gaps surface after acceleration ends.</p>
            </div>
          </div>
        </section>

        {/* ================= NEED ================= */}
        <section className="section reveal">
          <h2>What Accelerators Actually Need</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Rapid Claim Isolation</h4>
              <p>Quick separation of key assertions.</p>
            </div>

            <div className="info-card">
              <h4>Fair Comparability</h4>
              <p>Evaluation without rigid scoring systems.</p>
            </div>

            <div className="info-card">
              <h4>Independent Mentor Input</h4>
              <p>Reviews without peer influence.</p>
            </div>

            <div className="info-card">
              <h4>Credibility Visibility</h4>
              <p>Clear insight into strengths and gaps.</p>
            </div>

            <div className="info-card">
              <h4>Program Continuity</h4>
              <p>From intake to demo day and beyond.</p>
            </div>
          </div>
        </section>

        {/* ================= STRUCTURE ================= */}
        <section className="section reveal">
          <h2>Speed Without Guesswork</h2>

          <div className="flow-diagram">
            <div className="flow-step">
              <div className="flow-icon">📥</div>
              <h3>Program Intake</h3>
              <p>Applications enter at scale.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🧩</div>
              <h3>Claims Structured</h3>
              <p>Key statements are isolated quickly.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">📎</div>
              <h3>Evidence Aligned</h3>
              <p>Proof sits exactly where scrutiny exists.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">⚡</div>
              <h3>Parallel Review</h3>
              <p>Mentors evaluate without slowing momentum.</p>
            </div>
          </div>
        </section>

        {/* ================= EXAMPLE ================= */}
        <section className="section reveal">
          <h2>Example: Demo Day Is Not Diligence</h2>

          <p>
            Ten startups can deliver compelling demos. Prufyy shows which claims
            withstand scrutiny and which rely purely on presentation.
          </p>

          <div className="claim-evidence">
            <div className="claim-box">
              <strong>Claim</strong>
              <p>“Our product has strong user engagement.”</p>
            </div>

            <div className="link-line" />

            <div className="evidence-box">
              <strong>Evidence</strong>
              <ul>
                <li>Retention cohorts</li>
                <li>Usage frequency data</li>
                <li>Activation metrics</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ================= MULTI-MENTOR ================= */}
        <section className="section reveal">
          <h2>Multi-Mentor Input Without Groupthink</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Private Drafts</h4>
              <p>Mentors review independently.</p>
            </div>

            <div className="info-card">
              <h4>No Opinion Leakage</h4>
              <p>Others’ views remain hidden.</p>
            </div>

            <div className="info-card">
              <h4>Explicit Submission</h4>
              <p>Feedback is submitted intentionally.</p>
            </div>

            <div className="info-card">
              <h4>No Crowd Bias</h4>
              <p>No signaling loops influence judgment.</p>
            </div>
          </div>
        </section>

        {/* ================= CONTINUITY ================= */}
        <section className="section reveal">
          <h2>Continuity Beyond the Batch</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Credibility History</h4>
              <p>Past evaluations remain accessible.</p>
            </div>

            <div className="info-card">
              <h4>No Re-Pitching</h4>
              <p>Founders avoid starting from scratch.</p>
            </div>

            <div className="info-card">
              <h4>Investor Readiness</h4>
              <p>Follow-on investors get structured context.</p>
            </div>

            <div className="info-card">
              <h4>Reputation Compounding</h4>
              <p>Accelerator signal strengthens over time.</p>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section className="final-cta-section reveal">
          <h2>Run Faster Programs with Clearer Signal</h2>
          <p>
            Prufyy helps accelerators move quickly without sacrificing judgment
            or credibility.
          </p>
          <a href="/signup" className="btn-primary">
            Get Started with Prufyy
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Accelerator;
